// =============================================================================
// RegisterPage — New account registration for Portal
// =============================================================================
// After successful registration, user state is hydrated via /me call
// (not from register response) to confirm the cookie round-trip works.
// =============================================================================

import { useState, useMemo } from 'react';
import { useNavigate, Link, Navigate, useSearchParams } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/lib/auth-store';
import { api } from '@/lib/api';
import { Button, Input, Card, Spinner } from '@tkpod/ui';

// ---------------------------------------------------------------------------
// Password validation helpers
// ---------------------------------------------------------------------------

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

function getPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  score = Math.min(score, 4);

  const levels: Record<number, { label: string; color: string }> = {
    0: { label: 'Very Weak', color: 'bg-danger' },
    1: { label: 'Weak', color: 'bg-danger' },
    2: { label: 'Fair', color: 'bg-warning' },
    3: { label: 'Strong', color: 'bg-success' },
    4: { label: 'Very Strong', color: 'bg-success' },
  };

  return { score, ...levels[score] };
}

function validatePassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Must contain at least one lowercase letter';
  if (!/\d/.test(password)) return 'Must contain at least one number';
  if (!/[^a-zA-Z0-9]/.test(password)) return 'Must contain at least one special character';
  return null;
}

// ---------------------------------------------------------------------------
// RegisterPage Component
// ---------------------------------------------------------------------------

export function RegisterPage() {
  const { register, error, clearError, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read invite token from URL param or sessionStorage (set by /join/:id page)
  const inviteToken = useMemo(() => {
    const fromUrl = searchParams.get('invite');
    if (fromUrl) return fromUrl;
    return sessionStorage.getItem('inviteToken');
  }, [searchParams]);

  const inviteContext = useMemo(() => {
    if (!inviteToken) return null;

    // Try sessionStorage first (set by JoinPage with full context)
    const stored = sessionStorage.getItem('inviteContext');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          email: parsed.email as string,
          roleSlug: parsed.roleSlug as string,
          orgId: parsed.orgId as string | undefined,
          organizationName: parsed.organizationName as string | undefined,
        };
      } catch { /* fall through */ }
    }

    // Fallback: decode from token payload (legacy long-URL format)
    try {
      const payloadPart = inviteToken.split('.')[0];
      const decoded = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
      return {
        email: decoded.email as string,
        roleSlug: decoded.roleSlug as string,
        orgId: decoded.orgId as string,
      };
    } catch {
      return null;
    }
  }, [inviteToken]);

  const [email, setEmail] = useState(inviteContext?.email ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Wait for auth initialization before deciding
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
        <Spinner size="lg" />
      </div>
    );
  }

  // Authenticated user with invite token — show accept-invite UI
  if (isAuthenticated && inviteToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
        <div className="w-full max-w-md">
          <Card>
            <div className="p-6 text-center">
              <h2 className="text-xl font-bold text-text-primary mb-2">
                Accept Team Invitation
              </h2>
              <p className="text-sm text-text-secondary mb-6">
                You&apos;ve been invited to join a team
                {inviteContext ? ` as ${inviteContext.roleSlug}` : ''}.
              </p>
              {acceptError && (
                <div className="mb-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                  {acceptError}
                </div>
              )}
              <Button
                className="w-full"
                loading={submitting}
                onClick={async () => {
                  setSubmitting(true);
                  setAcceptError(null);
                  try {
                    await api.post('/api/v1/auth/accept-invite', { inviteToken });
                    await useAuthStore.getState().reinitialize();
                    navigate('/dashboard', { replace: true });
                  } catch (err) {
                    setAcceptError(err instanceof Error ? err.message : 'Failed to accept invitation.');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                Accept Invitation
              </Button>
              <Button
                variant="outline"
                className="w-full mt-3"
                onClick={() => void logout()}
              >
                Sign Out
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Already authenticated (no invite) — redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const passwordError = password ? validatePassword(password) : null;
  const confirmError =
    confirmPassword && password !== confirmPassword
      ? 'Passwords do not match'
      : null;
  const passwordStrength = password ? getPasswordStrength(password) : null;

  const canSubmit =
    email.length > 0 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    password.length >= 8 &&
    !passwordError &&
    !confirmError &&
    confirmPassword.length > 0 &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    clearError();
    setSubmitting(true);

    try {
      await register({
        email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(inviteToken ? { inviteToken } : {}),
      });
      sessionStorage.removeItem('inviteToken');
      sessionStorage.removeItem('inviteContext');
      navigate('/dashboard', { replace: true });
    } catch {
      // Error is captured in store — shown in UI below
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
            <span className="text-lg font-bold text-white">TK</span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Create Account</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Get started with Transfer Kingdom POD
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            {inviteContext && (
              <div className="rounded-lg bg-brand-50 border border-brand-200 px-4 py-3">
                <p className="text-sm font-medium text-brand-800">
                  You&apos;ve been invited to join
                  {inviteContext.organizationName ? ` ${inviteContext.organizationName}` : ' a team'}
                </p>
                <p className="text-sm text-brand-600 mt-1">
                  You&apos;ll join as <strong>{inviteContext.roleSlug}</strong>.
                  Create your account to accept the invitation.
                </p>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                required
                autoComplete="given-name"
                autoFocus
              />
              <Input
                label="Last Name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                required
                autoComplete="family-name"
              />
            </div>

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => !inviteContext && setEmail(e.target.value)}
              readOnly={!!inviteContext}
              placeholder="you@example.com"
              required
              autoComplete="email"
              className={inviteContext ? 'bg-surface-secondary cursor-not-allowed' : ''}
            />

            <div>
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                autoComplete="new-password"
                error={password && passwordError ? passwordError : undefined}
              />

              {passwordStrength && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i < passwordStrength.score
                            ? passwordStrength.color
                            : 'bg-surface-tertiary'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-text-tertiary">
                    {passwordStrength.label}
                  </p>
                </div>
              )}
            </div>

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              autoComplete="new-password"
              error={confirmError ?? undefined}
            />

            {/* Password requirements */}
            <div className="rounded-lg bg-surface-secondary p-3">
              <p className="text-xs font-medium text-text-secondary mb-2">
                Password Requirements
              </p>
              <ul className="space-y-1">
                <PasswordRequirement
                  met={password.length >= 8}
                  label="At least 8 characters"
                />
                <PasswordRequirement
                  met={/[A-Z]/.test(password)}
                  label="One uppercase letter"
                />
                <PasswordRequirement
                  met={/[a-z]/.test(password)}
                  label="One lowercase letter"
                />
                <PasswordRequirement
                  met={/\d/.test(password)}
                  label="One number"
                />
                <PasswordRequirement
                  met={/[^a-zA-Z0-9]/.test(password)}
                  label="One special character"
                />
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!canSubmit}
              loading={submitting}
            >
              {submitting ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        </Card>

        <p className="mt-6 text-center text-sm text-text-secondary">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-brand-600 hover:text-brand-500 transition-colors"
          >
            Sign in
          </Link>
        </p>

        <p className="mt-4 text-center text-xs text-text-tertiary">
          &copy; {new Date().getFullYear()} Transfer Kingdom. All rights reserved.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Password requirement checklist item
// ---------------------------------------------------------------------------

function PasswordRequirement({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      {met ? (
        <svg
          className="h-3.5 w-3.5 text-success"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m4.5 12.75 6 6 9-13.5"
          />
        </svg>
      ) : (
        <svg
          className="h-3.5 w-3.5 text-text-tertiary"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <circle cx="12" cy="12" r="9" />
        </svg>
      )}
      <span className={met ? 'text-text-primary' : 'text-text-tertiary'}>
        {label}
      </span>
    </li>
  );
}
